export const M6_UNIT_2 = {
  id: "m6-u2-predictions",
  title: "Unit 2: Making Predictions",
  topic: "Master WILL to express predictions, opinions, and future possibilities! Learn the crucial difference between WILL and GOING TO for natural, fluent English in any conversation!",
  iconType: "crystal-ball",
  grammarImage: "/grammar/grammar-m6-u2.jpg",
  grammarText: "Use WILL for predictions based on opinion vs GOING TO for predictions based on evidence. Essential for expressing thoughts about the future naturally!",

  sets: [

    // ===================================================================
    // SET 1: INTRODUCING WILL
    // ===================================================================
    {
      id: "m6-u2-s1",
      title: "Set 1: Introducing Will",
      context: `*Toronto, Canada — September, first day*

Sofía is a 22-year-old student from Mexico City. She has just arrived in Toronto to study English for one year.
She is standing outside her new university with her suitcase. Her phone is almost dead.
She calls her mother: "I think it will be amazing here, Mamá. I'm sure I will make many friends."
Her mother replies: "I bet you will love Canada! But I doubt it will be warm — bring a coat!"

*Toronto, Canadá — septiembre, primer día*

Sofía es una estudiante de 22 años de Ciudad de México. Acaba de llegar a Toronto para estudiar inglés durante un año.
Está parada afuera de su nueva universidad con su maleta. Su teléfono está casi sin batería.
Llama a su madre: "Creo que será increíble aquí, Mamá. Estoy segura de que haré muchos amigos."
Su madre responde: "¡Apuesto a que te encantará Canadá! Pero dudo que haga calor — ¡lleva un abrigo!"`,

      questions: [
        {
          type: "match",
          q: "Match the opinion expressions to their meanings:",
          pairs: [
            { left: "I think", right: "My opinion — not 100% certain" },
            { left: "I'm sure", right: "Very confident — almost certain" },
            { left: "I doubt", right: "I don't believe it will happen" }
          ],
          a: "completed",
          successTip: "These three expressions all introduce future predictions with WILL 💭. 'I think it will rain.' / 'I'm sure she will pass.' / 'I doubt he will come.' Learn them as a set!"
        },
        {
          type: "mc",
          q: "Sofía tells her mother: 'I think it ___ be amazing here!'",
          options: ["will", "is", "does"],
          a: "will",
          tip: "'I think' + future prediction = always use WILL after it.",
          successTip: "I think + WILL = the most natural way to give an opinion about the future 🔮. 'I think it will be great.' You'll use this every single day in English!"
        },
        {
          type: "input",
          q: "Her mother says: 'I ___ you will love Canada!'",
          a: "bet",
          alts: ["Bet","think","believe","guess","am sure","'m sure"],
          tip: "Informal way to say 'I'm sure'. One word.",
          successTip: "'I bet' = informal strong prediction 🎲. same meaning as 'I'm sure' but more casual. Very common with friends and family. 'I bet it'll be amazing!'"
        },
        {
          type: "order",
          q: "Traduce: 'Creo que Juan llegará tarde.'",
          words: ["I", "think", "Juan", "will", "be", "late."],
          a: "I think Juan will be late.",
          extra: ["is", "going", "to"],
          tip: "Pattern: I think + subject + WILL + base verb.",
          successTip: "'I think + WILL' = standard structure for opinions about the future 📝. Very common in everyday conversation! Notice: no 'to' after will."
        },
        {
          type: "mc",
          q: "Sofía says: 'I believe the weather ___ get better in spring.'",
          options: ["will", "is", "going"],
          a: "will",
          tip: "'I believe' = another opinion expression. Use WILL after it.",
          successTip: "'I believe' works just like 'I think' — both introduce WILL for future opinions 💬. 'I believe' is slightly more formal. Both are natural and correct!"
        },
        {
          type: "input",
          q: "Sofía thinks: 'I'm ___ I will make many friends here.'",
          a: "sure",
          alts: ["Sure","certain","positive", "convinced"],
          tip: "I'm ___ = very confident about the future. One word.",
          successTip: "'I'm sure + WILL' = expressing high confidence about the future 💪. Stronger than 'I think'. Use it when you're almost certain something will happen!"
        },
        {
          type: "mc",
          q: "Her mother says: 'I doubt it ___ be warm — bring a coat!'",
          options: ["will", "is", "does"],
          a: "will",
          tip: "'I doubt' = negative opinion. Still use WILL for the future verb.",
          successTip: "'I doubt it will...' = I don't think it will happen ❌. The negative meaning is in 'doubt', not in 'will'. 'I doubt she'll come.' / 'I doubt it'll work.'"
        },
        {
          type: "order",
          q: "Traduce: 'Estoy segura de que les encantará la fiesta.'",
          words: ["I'm", "sure", "they", "will", "love", "the", "party."],
          a: "I'm sure they will love the party.",
          extra: ["are", "going", "at"],
          tip: "Pattern: I'm sure + subject + WILL + base verb."
        },
        {
          type: "mc",
          q: "Which sentence uses the NEGATIVE opinion correctly?",
          options: ["I don't think it will rain.", "I think it won't rain.", "I think it will not rain."],
          a: "I don't think it will rain.",
          tip: "In English, put the negative with 'think', NOT with 'will'.",
          successTip: "'I don't think it will...' = the natural structure for negative opinions 🌤️. 'I think it won't...' is grammatical but less natural. Native speakers prefer the first!"
        },
        {
          type: "input",
          q: "Sofía tells her mother: 'I ___ the food in Canada will be very different!'",
          a: "bet",
          alts: ["Bet", "think", "Think", "'m sure", "am sure"],
          tip: "Any opinion expression works here. Choose the most informal one.",
          successTip: "You now know 5 opinion expressions: I think / I believe / I'm sure / I doubt / I bet 🎯. All introduce WILL for a future prediction. Master these and use them naturally!"
        }
      ]
    },

    // ===================================================================
    // SET 2: CONTRACTIONS & NEGATIVES
    // ===================================================================
    {
      id: "m6-u2-s2",
      title: "Set 2: Contractions & Negatives",
      context: `*Toronto — Sofía's apartment, same evening*

Sofía meets her new roommate, Priya, from India. They are making plans for the week.
"I'll show you the best places in the city," says Priya. "You'll love the food market on Saturday."
Sofía smiles. "I won't have much money this week — my bank card doesn't work yet."
"Don't worry," says Priya. "It'll be fine. My friend Dan will help you sort it out tomorrow."

*Toronto — Apartamento de Sofía, esa misma noche*

Sofía conoce a su nueva compañera de cuarto, Priya, de India. Están haciendo planes para la semana.
"Te mostraré los mejores lugares de la ciudad", dice Priya. "Te encantará el mercado de comida el sábado."
Sofía sonríe. "No tendré mucho dinero esta semana — mi tarjeta bancaria todavía no funciona."
"No te preocupes", dice Priya. "Todo estará bien. Mi amigo Dan te ayudará a resolverlo mañana."`,

      questions: [
        {
          type: "match",
          q: "Match the full forms to their contractions:",
          pairs: [
            { left: "I will", right: "I'll" },
            { left: "She will", right: "She'll" },
            { left: "They will not", right: "They won't" }
          ],
          a: "completed",
          successTip: "Native speakers use contractions in speech almost always 🗣️. 'I'll', 'she'll', 'won't' = natural English. 'I will', 'she will' = formal or for strong emphasis only."
        },
        {
          type: "mc",
          q: "Priya says in natural speech: '___ show you the best places in the city.'",
          options: ["I'll", "I will", "I am will"],
          a: "I'll",
          tip: "In spoken English, I'll is more natural than I will in most situations.",
          successTip: "I'll = I will 🎙️. In casual speech, 'I will' sounds formal or emphatic. 'I'll' is the natural default. Listen for it constantly in English films and podcasts!"
        },
        {
          type: "input",
          q: "Complete the contraction: He will = ___",
          a: "He'll",
          alts: ["he'll", "He'll", "He will"],
          tip: "Subject + apostrophe + ll. Two letters after the apostrophe.",
          successTip: "He'll / She'll / It'll / We'll / You'll / They'll — all follow the same pattern ✅. The only irregular one is 'will not' → 'won't' (not 'willn't'!). Won't is unique — learn it!"
        },
        {
          type: "order",
          q: "Traduce con contracción: 'Ella llegará pronto.'",
          words: ["She'll", "arrive", "soon."],
          a: "She'll arrive soon.",
          extra: ["She will", "arriving", "is"],
          tip: "Use the contraction She'll — not 'She will' — for natural speech."
        },
        {
          type: "mc",
          q: "Sofía says: 'I ___ have much money this week — my bank card doesn't work.'",
          options: ["won't", "will", "didn't"],
          a: "won't",
          tip: "Negative prediction about the future = WON'T (will not).",
          successTip: "Won't = will not ❌. The most common negative future form in speech. 'It won't work.' / 'She won't come.' / 'I won't be late.' Used constantly in natural conversation!"
        },
        {
          type: "input",
          q: "Complete: They ___ come to the party — they're too busy.",
          a: "won't",
          alts: ["Won't", "aren't going to", "will not"],
          tip: "Negative future prediction — contraction preferred.",
          successTip: "Won't = will not — much more natural than 'will not' in speech 🗣️. 'Will not' is reserved for strong emphasis: 'I WILL NOT do that again!' Otherwise, use won't."
        },
        {
          type: "mc",
          q: "Which is MORE NATURAL in spoken English?",
          options: ["We'll see you tomorrow.", "We will see you tomorrow.", "We see you tomorrow."],
          a: "We'll see you tomorrow.",
          tip: "In casual speech, contractions are more natural than full forms.",
          successTip: "'We'll' = casual and spoken ✓ / 'We will' = formal or emphatic ✓ / 'We see' = wrong for future ❌. 'We'll see you soon!' is one of the most common English goodbyes!"
        },
        {
          type: "order",
          q: "Traduce: 'No llegaremos tarde.'",
          words: ["We", "won't", "be", "late."],
          a: "We won't be late.",
          extra: ["willn't", "aren't", "going"],
          successTip: "Won't = will not — the most useful negative future contraction in English 🚫. 'We won't be late.' / 'She won't forget.' / 'It won't happen again.' Use it every day!"
        },
        {
          type: "match",
          q: "Match the subjects to their natural contractions:",
          pairs: [
            { left: "You will", right: "You'll" },
            { left: "It will", right: "It'll" },
            { left: "He will not", right: "He won't" }
          ],
          a: "completed",
          successTip: "All pronouns contract with 'll ✅. The only irregular contraction is 'will not' → 'won't' (never 'willn't'!). Won't is one of a kind — memorise it separately!"
        },
        {
          type: "mc",
          q: "Priya reassures Sofía: 'Don't worry — ___ be fine!'",
          options: ["it'll", "it's will", "it won't"],
          a: "it'll",
          tip: "Reassurance in casual speech = contraction. It'll = it will.",
          successTip: "'It'll be fine!' = one of the most common reassurances in English 😊. 'It'll be okay.' / 'It'll work out.' / 'It'll all make sense soon.' Very warm and natural!"
        }
      ]
    },

    // ===================================================================
    // SET 3: WILL vs GOING TO
    // ===================================================================
    {
      id: "m6-u2-s3",
      title: "Set 3: Will vs Going To",
      context: `*Toronto — Tuesday morning, Sofía's first week*

Sofía is walking to university with Priya. The sky is full of dark, heavy clouds.
"Look at those clouds!" says Priya. "It's going to rain — I'm sure of it."
Sofía laughs. "I think it will be okay. It was sunny this morning!"
Priya shakes her head. "I checked the forecast. It's going to be very cold all week."
"Well," says Sofía, "I think winter in Toronto will be a real adventure!"

*Toronto — martes por la mañana, primera semana de Sofía*

Sofía camina a la universidad con Priya. El cielo está lleno de nubes oscuras y pesadas.
"¡Mira esas nubes!", dice Priya. "Va a llover — estoy segura."
Sofía se ríe. "Creo que estará bien. ¡Esta mañana había sol!"
Priya niega con la cabeza. "Revisé el pronóstico. Va a hacer mucho frío toda la semana."
"Bueno," dice Sofía, "¡creo que el invierno en Toronto será una verdadera aventura!"`,

      questions: [
        {
          type: "match",
          q: "Match the situation to the correct future form:",
          pairs: [
            { left: "I see dark clouds right now", right: "It's going to rain" },
            { left: "I think it's a good team", right: "They will win" },
            { left: "I have my ticket already", right: "I'm going to fly" }
          ],
          a: "completed",
          tip: "Evidence you can see or know = GOING TO. Opinion in your head = WILL.",
          successTip: "The core rule: GOING TO = evidence 👁️ / WILL = opinion 💭. This is the most important distinction in this unit — master it and everything else follows!"
        },
        {
          type: "mc",
          q: "Priya points at dark clouds: 'It ___ rain — I can see it!'",
          options: ["is going to", "will", "might"],
          a: "is going to",
          tip: "She SEES the clouds. Visible evidence = GOING TO, not WILL.",
          successTip: "Visible evidence → GOING TO ☁️. When you react to something you can see, use GOING TO. The clouds ARE there — it's not just an opinion. 'Look! It's going to rain!'"
        },
        {
          type: "mc",
          q: "Sofía replies: 'I think it ___ be okay — it was sunny this morning!'",
          options: ["will", "is going to", "goes"],
          a: "will",
          tip: "'I think' = personal opinion with no direct visible evidence. Use WILL.",
          successTip: "'I think' → WILL 💭. Sofía gives her personal opinion, not reacting to visible evidence. This contrast with Priya's 'going to' shows the rule perfectly in one scene!"
        },
        {
          type: "order",
          q: "Traduce: '¡Mira! El número 5 va a ganar.'",
          words: ["Look!", "Number", "5", "is", "going", "to", "win."],
          a: "Look! Number 5 is going to win.",
          extra: ["was", "thinks", "I"],
          tip: "'Look!' = you see evidence happening right now. Use GOING TO.",
          successTip: "'Look!' almost always triggers GOING TO 👀. You're reacting to something visible. 'Look, she's going to fall!' / 'Look, it's going to snow!' React → GOING TO!"
        },
        {
          type: "mc",
          q: "Sofía booked her flights last week. She ___ fly to Vancouver next month.",
          options: ["is going to", "will", "goes"],
          a: "is going to",
          tip: "Flights already booked = confirmed plan with evidence. Use GOING TO.",
          successTip: "Confirmed plans = GOING TO ✈️. Tickets, reservations, appointments all count as 'evidence' of a plan. 'She's going to start a new job.' / 'We're going to visit London.'"
        },
        {
          type: "input",
          q: "Priya checked the weather app. She says: 'It ___ ___ ___ ___ very cold this week.'",
          a: "is going to be",
          alts: ["Is going to be", "'s going to be","s going to be", "is expected to be", "is likely to be", "will probably be", "might also be"],
          tip: "She checked the forecast = evidence. Use the full GOING TO form. Three words.",
          successTip: "Forecast = evidence 📱. When you quote a forecast or data, GOING TO is correct. You're reporting evidence — not giving a personal guess like WILL would suggest!"
        },
        {
          type: "mc",
          q: "Watch out! You ___ fall on that ice!",
          options: ["are going to", "will", "are"],
          a: "are going to",
          tip: "Immediate visible danger = GOING TO. You can see it about to happen.",
          successTip: "Warnings use GOING TO because you see the danger ⚠️. 'Watch out, you're going to fall!' / 'Careful, he's going to spill that!' See it → GOING TO. It's instant!"
        },
        {
          type: "order",
          q: "Traduce: 'Creo que María será una gran doctora.' (personal opinion)",
          words: ["I", "think", "Maria", "will", "be", "a", "great", "doctor."],
          a: "I think Maria will be a great doctor.",
          extra: ["is going to", "going", "she"],
          tip: "'I think' = your personal opinion, no direct evidence. Use WILL."
        },
        {
          type: "mc",
          q: "She studies hard every day. I'm sure she ___ pass the exam.",
          options: ["will", "is going to", "passes"],
          a: "will",
          tip: "'I'm sure' = strong personal opinion. You are predicting — not citing a scheduled result.",
          successTip: "Even with some evidence (studying), if you use 'I think / I'm sure' = WILL 🎓. GOING TO works if you say 'Look how hard she's studying — she's going to pass!' Phrasing decides!"
        },
        {
          type: "input",
          q: "Oh no! The car ___ ___ to crash! (you see it about to happen right now)",
          a: "is going",
          alts: ["Is going", "'s going"],
          tip: "Visible, immediate danger = GOING TO. Three words.",
          successTip: "Immediate visible predictions = GOING TO 🚗💥. WILL = future opinion. GOING TO = something you can see happening RIGHT NOW. The difference is in what your eyes see!"
        }
      ]
    },

    // ===================================================================
    // SET 4: EXPRESSING OPINIONS
    // ===================================================================
    {
      id: "m6-u2-s4",
      title: "Set 4: Expressing Opinions",
      context: `*Toronto University — Thursday, orientation week*

Sofía is at the university orientation. She meets two new classmates: Leo from Brazil and Amara from Nigeria.
They are talking about their predictions for the school year ahead.
"I don't think the first semester will be easy," says Leo. "The grammar is very complex."
Amara shakes her head: "I'm certain that we'll improve quickly. We study every day."
Sofía smiles: "I guess it will be hard — but I bet we'll all pass!"

*Universidad de Toronto — jueves, semana de orientación*

Sofía está en la orientación universitaria. Conoce a dos nuevos compañeros: Leo de Brasil y Amara de Nigeria.
Hablan sobre sus predicciones para el año escolar que comienza.
"No creo que el primer semestre sea fácil", dice Leo. "La gramática es muy compleja."
Amara niega con la cabeza: "Estoy segura de que mejoraremos rápidamente. Estudiamos todos los días."
Sofía sonríe: "Supongo que será difícil — ¡pero apuesto a que todos pasaremos!"`,

      questions: [
        {
          type: "mc",
          q: "Leo says: 'I think ___ the first semester will be very difficult.'",
          options: ["that", "what", "because"],
          a: "that",
          tip: "'That' after 'I think' is optional. 'I think that...' and 'I think...' are both correct.",
          successTip: "'That' is optional after I think, I believe, I'm sure 📝. 'I think (that) it will rain.' Both forms are 100% correct. In speech, most people leave 'that' out."
        },
        {
          type: "order",
          q: "Traduce: 'No creo que ella venga a la fiesta.'",
          words: ["I", "don't", "think", "she'll", "come", "to", "the", "party."],
          a: "I don't think she'll come to the party.",
          extra: ["won't", "not", "going"],
          tip: "Negative opinion = 'I don't think...', NOT 'I think she won't...'",
          successTip: "English puts the negative with 'think' 🚫. 'I don't think she'll come' = natural. 'I think she won't come' = grammatical but less common. This pattern is essential to master!"
        },
        {
          type: "mc",
          q: "Which is MORE NATURAL in English?",
          options: ["I don't think it'll work.", "I think it won't work.", "I think it will not work."],
          a: "I don't think it'll work.",
          tip: "Native speakers put the negative with 'think', not with 'will'.",
          successTip: "'I don't think + will' = the preferred negative opinion structure 💬. Much more natural than 'I think + won't'. Listen for this in English TV — you'll hear it constantly!"
        },
        {
          type: "input",
          q: "Leo says: 'I ___ ___ he'll pass the exam — he never studies.'",
          a: "don't think",
          alts: ["Don't think","doubt that", "don't think", "don't believe", "can't imagine", "really doubt"],
          tip: "Negative opinion. Two words — the negative goes with 'think'.",
          successTip: "'I don't think + will' is the natural negative opinion structure in English 📚. 'I don't think it'll rain.' / 'I don't think she'll come.' Learn this pattern once and use it forever!"
        },
        {
          type: "mc",
          q: "Amara says: 'I'm certain ___ we'll all improve by December.'",
          options: ["that", "what", "about"],
          a: "that",
          tip: "'That' is optional after 'I'm certain'. same rule as 'I think (that)'.",
          successTip: "'I'm certain' = stronger than 'I think' 💯. 'I'm certain (that) it will work.' Also: 'I'm convinced', 'I'm positive' = same level of confidence. Use them for strong opinions!"
        },
        {
          type: "order",
          q: "Traduce: 'Dudo que llueva mañana.'",
          words: ["I", "doubt", "it'll", "rain", "tomorrow."],
          a: "I doubt it'll rain tomorrow.",
          extra: ["won't", "don't", "think"],
          tip: "'I doubt' = negative opinion without the word 'not'. The negativity is inside 'doubt' itself.",
          successTip: "'I doubt' = I don't think it will happen 🤔. No 'not' needed! 'I doubt he'll come.' / 'I doubt it'll work.' Very natural and slightly more elegant than 'I don't think'."
        },
        {
          type: "match",
          q: "Match the opinion expressions to their certainty level:",
          pairs: [
            { left: "I guess", right: "About 40% certain" },
            { left: "I think", right: "About 60% certain" },
            { left: "I'm certain", right: "About 95% certain" }
          ],
          a: "completed",
          successTip: "Certainty levels let you be precise in English 🎯. I guess (weak) → I think (medium) → I believe (medium-high) → I'm sure / I'm certain (strong). Match your words to how confident you actually feel!"
        },
        {
          type: "mc",
          q: "Which shows the STRONGEST opinion about the future?",
          options: ["I'm positive they'll win.", "I think they'll win.", "I guess they'll win."],
          a: "I'm positive they'll win.",
          tip: "'I'm positive' = 100% sure. 'I think' ≈ 60%. 'I guess' ≈ 40%.",
          successTip: "Opinion strength matters in conversation 💪. 'I'm positive!' shows conviction. 'I guess...' shows uncertainty. Choosing the right level makes your English sound natural and precise!"
        },
        {
          type: "input",
          q: "Sofía says: 'I ___ we'll all pass! We're a great group.'",
          a: "bet",
          alts: ["Bet", "'m sure", "am sure", "'m certain", "am certain", "'m positive", "am positive"],
          tip: "Strong, informal opinion. Use a high-certainty expression.",
          successTip: "'I bet' = informal strong opinion 🎲. Common in casual speech. 'I bet it'll be fun!' / 'I bet he'll be late again.' Very natural with friends and classmates!"
        },
        {
          type: "order",
          q: "Traduce: 'Supongo que el examen será difícil.'",
          words: ["I", "guess", "the", "exam", "will", "be", "difficult."],
          a: "I guess the exam will be difficult.",
          extra: ["probably", "think", "going"],
          successTip: "'I guess' = weak opinion, not very confident 🤷. Less certain than 'I think'. 'I guess it'll be okay' = you think it might be okay, but you're not sure. Very common in spoken English!"
        }
      ]
    },

    // ===================================================================
    // SET 5: PREDICTION WORDS
    // ===================================================================
    {
      id: "m6-u2-s5",
      title: "Set 5: Prediction Words",
      context: `*Toronto — Saturday afternoon, hockey game*

It's Sofía's first weekend in Canada. Priya has taken her to watch a local hockey game.
Sofía has never seen ice hockey before and is completely amazed.
"The blue team is much stronger," says Priya. "They'll definitely win tonight."
"I think the red team will probably score at least once," says Sofía.
The announcer's voice fills the arena: "Tonight's game will definitely be one to remember!"

*Toronto — sábado por la tarde, partido de hockey*

Es el primer fin de semana de Sofía en Canadá. Priya la llevó a ver un partido de hockey local.
Sofía nunca ha visto hockey sobre hielo y está completamente asombrada.
"El equipo azul es mucho más fuerte", dice Priya. "Definitivamente ganarán esta noche."
"Creo que el equipo rojo probablemente anotará al menos una vez", dice Sofía.
La voz del locutor llena el recinto: "¡El partido de esta noche definitivamente será uno para recordar!"`,

      questions: [
        {
          type: "match",
          q: "Match the prediction words to their certainty level:",
          pairs: [
            { left: "Definitely", right: "100% sure" },
            { left: "Probably", right: "70-80% sure" },
            { left: "Maybe", right: "50% sure" }
          ],
          a: "completed",
          successTip: "These words go AFTER will or 'll: 'She'll definitely come.' / 'He'll probably be late.' / 'It'll maybe work.' 📊 Position: subject + 'll + prediction word + verb. Learn this order!"
        },
        {
          type: "mc",
          q: "Priya says: 'The blue team is much stronger. They'll ___ win tonight.'",
          options: ["definitely", "maybe", "never"],
          a: "definitely",
          tip: "'Much stronger' = strong evidence → very high certainty. Use DEFINITELY.",
          successTip: "DEFINITELY = 100% certain 💯. 'They'll definitely win.' Use it when you have strong reasons. Much more confident than 'probably'. 'She'll definitely pass — she studies every day!'"
        },
        {
          type: "input",
          q: "Sofía says: 'I think the red team will ___ score at least once.'",
          a: "probably",
          alts: ["Probably", "maybe", "Maybe"],
          tip: "Not 100% sure but fairly likely. One word — around 70-80% certainty.",
          successTip: "PROBABLY = likely but not certain 📈. Goes after 'll: 'It'll probably rain.' / 'She'll probably be late.' Also works with full will: 'It will probably rain.' Both positions correct!"
        },
        {
          type: "order",
          q: "Traduce: 'Probablemente llegue tarde.'",
          words: ["I'll", "probably", "be", "late."],
          a: "I'll probably be late.",
          extra: ["will", "certainly", "definitely"],
          tip: "Word order: Subject + 'll + PROBABLY + verb. Probably goes AFTER 'll.",
          successTip: "Word order: I'll PROBABLY be late ✓ / PROBABLY I'll be late ✗ 📐. This is different from Spanish!"
        },
        {
          type: "mc",
          q: "Sofía doesn't know who will win. She says: 'It ___ be a draw.'",
          options: ["might", "will definitely", "won't"],
          a: "might",
          tip: "Not sure at all — 50% or less. MIGHT expresses possibility, not prediction.",
          successTip: "MIGHT = 50% possibility 🤔. 'It might rain' = possible. 'It will rain' = I predict."
        },
        {
          type: "mc",
          q: "The blue team has won the last 10 games in a row. They'll ___ win again.",
          options: ["definitely", "maybe", "perhaps"],
          a: "definitely",
          tip: "10 wins in a row = very strong evidence. Use DEFINITELY (100%).",
          successTip: "DEFINITELY shows conviction 💪. 'She studies every day — she'll definitely pass.' 'He's the best player — they'll definitely win.' Strong consistent evidence = DEFINITELY!"
        },
        {
          type: "input",
          q: "Sofía doesn't know the teams well. She says: 'It'll ___ be an exciting game either way!'",
          a: "probably",
          alts: ["Probably", "definitely", "Definitely"],
          tip: "Any prediction word fits — choose the one that feels most natural to you.",
          successTip: "Prediction words make your English sound natural and nuanced 🎯. 'It'll definitely / probably happen.' Each word signals your confidence level. Use them freely in conversation!"
        },
        {
          type: "order",
          q: "Traduce: 'Definitivamente iré a la próxima fiesta.'",
          words: ["I'll", "definitely", "go", "to", "the", "next", "party."],
          a: "I'll definitely go to the next party.",
          extra: ["probably", "maybe", "going"],
          successTip: "DEFINITELY + WILL = 100% certain future commitment 🎉. 'I'll definitely be there!' Very common way to confirm plans enthusiastically. Much stronger than 'I'll probably go.'"
        },
        {
          type: "match",
          q: "Match the situations to the most appropriate prediction word:",
          pairs: [
            { left: "He always wins — no doubt", right: "He'll definitely win" },
            { left: "Good team but tough opponent", right: "They'll probably win" },
            { left: "No idea who is better", right: "They might win" }
          ],
          a: "completed",
          successTip: "Match your certainty to the situation 🎯. Definitely (100%) / Probably (70-80%) / Might (50%). Use the word that reflects your actual level of confidence — this is native-level precision!"
        },
        {
          type: "mc",
          q: "The sky is completely clear and sunny all day. It ___ rain today.",
          options: ["definitely won't", "probably will", "might"],
          a: "definitely won't",
          tip: "Clear sunny sky all day = 100% sure it WON'T rain. Use DEFINITELY WON'T.",
          successTip: "Prediction words work with negatives too! 'Definitely won't' = 100% certain it will NOT happen 🚫. 'She definitely won't come.' / 'It definitely won't rain.' Applies the same certainty scale!"
        }
      ]
    },
    // ===================================================================
    // SET 6: ADVANCED PREDICTION WORDS
    // ===================================================================
    {
      id: "m6-u2-s6",
      title: "Set 6: Advanced Prediction Words",
      context: `*Toronto University — Week 3, Career Fair*

Sofía's university is holding a career fair. She attends a formal presentation by a Canadian company.
The HR manager, Ms. Patterson, is talking about the future of the company.
"With our new strategy, we will certainly expand into Latin America," she says.
"The market will likely grow 30% by next year. Perhaps we'll open offices in Mexico City."
Sofía is excited. She writes in her notebook: "Perhaps I'll apply for a job here after my studies!"

*Universidad de Toronto — Semana 3, Feria de Carreras*

La universidad de Sofía organiza una feria de carreras. Ella asiste a una presentación formal de una empresa canadiense.
La gerente de recursos humanos, la Sra. Patterson, habla sobre el futuro de la empresa.
"Con nuestra nueva estrategia, ciertamente expandiremos hacia América Latina", dice.
"El mercado probablemente crecerá un 30% el próximo año. Quizás abramos oficinas en Ciudad de México."
Sofía está emocionada. Escribe en su cuaderno: "¡Quizás solicite un trabajo aquí después de mis estudios!"`,

      questions: [
        {
          type: "match",
          q: "Match the formal prediction words to their informal equivalents:",
          pairs: [
            { left: "Perhaps", right: "Maybe" },
            { left: "Certainly", right: "Definitely" },
            { left: "Likely", right: "Probably" }
          ],
          a: "completed",
          successTip: "Formal vs informal prediction words 🎩👕. Perhaps/Certainly/Likely = formal (emails, presentations, writing). Maybe/Definitely/Probably = informal (casual speech). Both sets are correct — context decides which to use!"
        },
        {
          type: "mc",
          q: "Ms. Patterson says: 'It's possible but not confirmed. We'll ___ open an office in Mexico City.'",
          options: ["perhaps", "definitely", "never"],
          a: "perhaps",
          tip: "'Perhaps' = maybe, but more formal. Common in business presentations.",
          successTip: "'Perhaps' = 'maybe' in formal English 🎩. 'I'll perhaps attend' / 'We'll perhaps expand'. Very common in UK English and formal business contexts. More polished than 'maybe'!"
        },
        {
          type: "input",
          q: "Ms. Patterson says: 'With her experience, she'll ___ get the promotion.'",
          a: "certainly",
          alts: ["Certainly", "definitely", "Definitely", "likely", "Likely"],
          tip: "Almost 100% certain, formal register. One word.",
          successTip: "'Certainly' = 'definitely' in formal English 💼. Very common in business and professional settings. 'We'll certainly consider your proposal.' Signals high confidence professionally!"
        },
        {
          type: "order",
          q: "Traduce: 'Tal vez vaya al cine esta noche.'",
          words: ["I'll", "perhaps", "go", "to", "the", "cinema", "tonight."],
          a: "I'll perhaps go to the cinema tonight.",
          extra: [ "definitely", "going"],
          tip: "'Perhaps' = 'tal vez' in formal register. Notice 'cinema' (British) vs 'movies' (American).",
          successTip: "'Cinema' (British) vs 'movies' (American) 🎬 — and 'perhaps' is more common in British English. 'Perhaps' can go at the start too: 'Perhaps I'll go...' Both positions are correct!"
        },
        {
          type: "mc",
          q: "Ms. Patterson says: 'The market will ___ grow — all our data supports this.'",
          options: ["likely", "maybe", "perhaps"],
          a: "likely",
          tip: "'Likely' = 'probably' in formal/British English. Very common in reports and presentations.",
          successTip: "'Likely' = probably, but more formal 🇬🇧. 'It will likely be the best option.' / 'She'll likely attend.' Very common in British English and written reports. Sounds polished and professional!"
        },
        {
          type: "mc",
          q: "The weather is bad. Sofía thinks: 'We'll ___ not have the outdoor event, but we'll see.'",
          options: ["most likely", "definitely", "certainly"],
          a: "most likely",
          tip: "'Most likely' = very probably (80-90%). Stronger than 'probably' alone.",
          successTip: "'Most likely' = stronger than 'probably', less absolute than 'definitely' 📊. 'She'll most likely win.' / 'It'll most likely rain.' Very natural in spoken and written English. Use it for high-confidence predictions!"
        },
        {
          type: "input",
          q: "There is a good chance it will happen. 'It'll ___ work out.'",
          a: "likely",
          alts: ["Likely", "probably", "Probably", "most likely"],
          tip: "High probability, formal register. One word.",
          successTip: "'Likely' and 'probably' are interchangeable in most contexts, but 'likely' sounds more formal 📝. In a business email: 'This will likely resolve the issue.' In conversation: 'It'll probably be fine.' Both are natural!"
        },
        {
          type: "order",
          q: "Traduce: 'Ciertamente será un desafío.'",
          words: ["It'll", "certainly", "be", "a", "challenge."],
          a: "It'll certainly be a challenge.",
          extra: ["not", "probably", "perhaps"],
          successTip: "'Certainly' signals confidence in formal contexts 💼. 'It'll certainly be a challenge.' Great for professional conversations, interviews, and formal presentations. Sounds educated and confident!"
        },
        {
          type: "match",
          q: "Match the contexts to the most appropriate prediction words:",
          pairs: [
            { left: "Casual conversation with friends", right: "Maybe, probably" },
            { left: "Business email or presentation", right: "Likely, certainly" },
            { left: "Academic essay or report", right: "Perhaps, presumably" }
          ],
          a: "completed",
          successTip: "Register matters in English! 👥💼📚 Casual = maybe, probably. Business = likely, certainly. Academic = perhaps, presumably. Using the right word for the right context is a key sign of advanced English — it shows you understand the social dimension of language!"
        },
        {
          type: "mc",
          q: "Sofía writes in her notebook: 'I have no doubt — this company will ___ be successful.'",
          options: ["certainly", "maybe", "perhaps"],
          a: "certainly",
          tip: "'No doubt' = 100% confident. Use CERTAINLY (formal) or DEFINITELY (informal).",
          successTip: "'Certainly' in formal writing shows professional confidence 📈. 'This plan will certainly succeed.' / 'She will certainly be an asset to the team.' It's strong, polished, and very common in business English. Sofía is becoming a great professional writer!"
        }
      ]
    },

    // ===================================================================
    // SET 7: WEATHER & SPORTS PREDICTIONS
    // ===================================================================
    {
      id: "m6-u2-s7",
      title: "Set 7: Weather & Sports Predictions",
      context: `*Toronto — Saturday afternoon, Stanley Cup Playoffs, Week 4*

Sofía is at a sports bar with Priya and their new friend Dan, a Canadian student.
They are watching the Toronto Maple Leafs hockey game on a huge screen.
Outside, the weather is changing fast. Dan looks out the window.
"Look at those clouds — it's going to snow tonight," Dan says.
"I think the game will go into overtime," says Priya. "The Leafs always make it dramatic!"
Sofía laughs and tries to join the conversation: "I think... the Maple Leafs will definitely win? 
Dan grins. "Now you sound like a Canadian!"

*Toronto — sábado por la tarde, Playoffs de la Copa Stanley, Semana 4*

Sofía está en un bar deportivo con Priya y su nuevo amigo Dan, un estudiante canadiense.
Están viendo el partido de hockey de los Toronto Maple Leafs en una pantalla enorme.
Afuera, el clima está cambiando rápidamente. Dan mira por la ventana.
"Mira esas nubes — va a nevar esta noche", dice Dan.
"Creo que el partido irá a tiempo extra", dice Priya. "¡Los Leafs siempre lo hacen dramático!"
Sofía ríe e intenta unirse a la conversación: "Creo que... ¿los Maple Leafs definitivamente ganarán?"
Dan sonríe. "¡Ahora suenas como canadiense!"`,

      questions: [
        {
          type: "mc",
          q: "Dan looks at clouds outside and says: 'It ___ snow tonight.'",
          options: ["is going to", "will", "might"],
          a: "is going to",
          tip: "He SEES the clouds. Visible evidence right now = GOING TO, not WILL.",
          successTip: "Visible evidence → GOING TO ☁️❄️. Dan reacts to what he can SEE. This is the classic GOING TO trigger in weather talk. Listen for 'Look at those clouds — it's going to rain/snow!' in real English conversations!"
        },
        {
          type: "mc",
          q: "Priya says: 'I think the game ___ go into overtime — the Leafs always do this!'",
          options: ["will", "is going to", "goes"],
          a: "will",
          tip: "'I think' = personal opinion. No direct evidence that overtime will happen. Use WILL.",
          successTip: "'I think' → WILL 💭. Priya gives her personal prediction based on experience, not visible evidence. Compare: Dan saw clouds (GOING TO). Priya just thinks it'll happen (WILL). Same scene, two different grammar choices!"
        },
        {
          type: "order",
          q: "Traduce: 'El pronóstico dice que nevará.'",
          words: ["The", "forecast", "says", "it's", "going", "to", "snow."],
          a: "The forecast says it's going to snow.",
          extra: ["will", "I", "think"],
          tip: "'The forecast says' = citing evidence. Use GOING TO when you report what a forecast says.",
          successTip: "When quoting a forecast = GOING TO 🌨️. You're reporting evidence, not giving your personal guess. 'The forecast says it's going to rain.' / 'The weather app says it's going to be sunny.' This is key for real weather conversations!"
        },
        {
          type: "input",
          q: "Dan says: 'In Canada, winter is long. It'll ___ snow a lot this year.'",
          a: "definitely",
          alts: ["Definitely", "probably", "Probably", "certainly", "Certainly"],
          tip: "Canadian winter = very predictable. Any high-certainty prediction word works.",
          successTip: "Canadian winters = extremely predictable ❄️🇨🇦. 'It'll definitely snow a lot' / 'It'll probably be very cold' — both natural. Winter weather talk is the most common small talk in Canada from October to April!"
        },
        {
          type: "mc",
          q: "Dan says: 'I bet the Leafs ___ win tonight — they've been amazing this season!'",
          options: ["will", "are going to", "are"],
          a: "will",
          tip: "'I bet' = strong personal opinion. Opinion expressions always pair with WILL.",
          successTip: "'I bet + WILL' = confident sports prediction 🏒. 'I bet they'll win!' / 'I bet she'll score!' Very energetic and natural in sports talk. 'I bet' shows enthusiasm and conviction — perfect for watching games with friends!"
        },
        {
          type: "order",
          q: "Traduce: 'Creo que el equipo de casa ganará el Super Bowl.'",
          words: ["I", "think", "the", "home", "team", "will", "win", "the", "Super", "Bowl."],
          a: "I think the home team will win the Super Bowl.",
          extra: ["is going to", "probably", "definitely"],
          tip: "'I think' = your sports opinion. WILL is the correct choice for opinion-based predictions.",
          successTip: "Sports predictions = one of the most natural uses of WILL 🏈🏒. 'I think they'll win.' / 'I believe she'll score.' Americans love the Super Bowl, Canadians love hockey, Australians love rugby — all use WILL for game predictions!"
        },
        {
          type: "mc",
          q: "The Stanley Cup playoffs are starting. Sofía asks: 'Who do you think ___ win?'",
          options: ["will", "is going to", "is"],
          a: "will",
          tip: "'Who do you think...' asks for someone's opinion. Opinion questions use WILL.",
          successTip: "'Who do you think will win?' = asking for opinions 🏆. This question triggers WILL because you're asking about someone's personal prediction. 'Who do you think will get the Oscar?' / 'Who do you think will win the election?' — all WILL!"
        },
        {
          type: "input",
          q: "It's very cloudy outside. Sofía says: 'I think it ___ rain before the game ends.'",
          a: "will",
          alts: ["Will", "'ll"],
          tip: "'I think' = opinion. Even with visible clouds, 'I think' forces WILL — not GOING TO.",
          successTip: "Phrasing decides grammar! 🎯 'I THINK it will rain' (WILL = opinion). 'LOOK at those clouds — it's going to rain!' (GOING TO = you react to evidence). same clouds, but what you SAY determines the grammar. The speaker's framing controls everything!"
        },
        {
          type: "match",
          q: "Match the sports to their countries and typical prediction talk:",
          pairs: [
            { left: "Super Bowl predictions", right: "USA 🏈" },
            { left: "Cricket match predictions", right: "UK / Australia / India 🏏" },
            { left: "Hockey playoff predictions", right: "Canada 🏒" }
          ],
          a: "completed",
          successTip: "Sports culture = prediction culture 🌍! Every country uses the future for sports talk. In Canada: 'Do you think the Leafs will win tonight?' In the US: 'Who do you think will win the Super Bowl?' In the UK: 'I think United will probably lose...' All use WILL for opinions!"
        },
        {
          type: "mc",
          q: "Dan says: 'The Leafs are the best team in the playoffs. They'll ___ win the Cup!'",
          options: ["definitely", "maybe", "perhaps"],
          a: "definitely",
          tip: "'Best team in the playoffs' = strong reason. Use DEFINITELY for high-confidence predictions.",
          successTip: "DEFINITELY = maximum confidence 💪🏒. 'They're the best — they'll definitely win!' Use 'definitely' when you have strong reasons AND you're very committed to your prediction. It shows passion and conviction — perfect for sports conversations!"
        }
      ]
    },

    // ===================================================================
    // SET 8: ELECTIONS & TECHNOLOGY
    // ===================================================================
    {
      id: "m6-u2-s8",
      title: "Set 8: Elections & Technology",
      context: `*Toronto University — Week 5, Current Events class*

Sofía's English class is discussing current world events.
The professor, Dr. Chen, asks students to make predictions using WILL and GOING TO correctly.
"According to the latest polls, the Green Party candidate is going to win the election," says Leo.
"I believe renewable energy will change everything in the next 20 years," adds Amara.
Sofía raises her hand: "I think AI will probably replace some jobs — but I'm sure it will create new ones too."
Dr. Chen smiles. "Excellent use of WILL and GOING TO, everyone!"

*Universidad de Toronto — Semana 5, clase de Actualidades*

La clase de inglés de Sofía discute eventos mundiales actuales.
La profesora, Dra. Chen, pide a los estudiantes que hagan predicciones usando WILL y GOING TO correctamente.
"Según las últimas encuestas, la candidata del Partido Verde va a ganar las elecciones", dice Leo.
"Creo que la energía renovable cambiará todo en los próximos 20 años", añade Amara.
Sofía levanta la mano: "Creo que la IA probablemente reemplazará algunos trabajos — ¡pero estoy segura de que también creará nuevos!"
La Dra. Chen sonríe. "¡Excelente uso de WILL y GOING TO, a todos!"`,

      questions: [
        {
          type: "mc",
          q: "Amara says: 'I believe renewable energy ___ change everything in 20 years.'",
          options: ["will", "is going to", "changes"],
          a: "will",
          tip: "'I believe' = personal opinion about the future. Opinion expressions always pair with WILL.",
          successTip: "'I believe + WILL' = expressing a strong personal conviction about the future 🌍. Climate change, technology, society — these big-picture predictions use WILL because they're based on your worldview, not specific data you're citing. Very natural in class discussions!"
        },
        {
          type: "order",
          q: "Traduce: 'Creo que los robots reemplazarán muchos trabajos.'",
          words: ["I", "think", "robots", "will", "replace", "many", "jobs."],
          a: "I think robots will replace many jobs.",
          extra: ["are going to", "probably", "definitely"],
          tip: "'I think' = personal opinion. WILL is correct. No 'to' after will.",
          successTip: "Technology predictions = huge conversation topic in 2026 🤖. 'I think robots will replace...' / 'I believe AI will change...' / 'I'm sure technology will improve...' These WILL + opinion expressions are essential for discussing the future of work and society!"
        },
        {
          type: "mc",
          q: "Leo says: 'According to polls, the Green Party candidate ___ win by a large margin.'",
          options: ["is going to", "will", "might"],
          a: "is going to",
          tip: "'According to polls' = citing data/evidence. Evidence-based predictions use GOING TO.",
          successTip: "Polls/surveys = evidence → GOING TO 📊. When you cite data, studies, or external sources, use GOING TO because you're reporting evidence — not giving your personal opinion. 'According to research, electric cars are going to dominate by 2035.'"
        },
        {
          type: "input",
          q: "Sofía says: 'Electric cars will ___ become more popular in the next 10 years.'",
          a: "probably",
          alts: ["Probably", "likely", "Likely", "certainly", "Certainly", "definitely", "Definitely"],
          tip: "Not 100% certain but very likely. One word, 70-80% certainty.",
          successTip: "Environmental predictions with 'probably' sound balanced and educated 🌱. 'Electric cars will probably dominate by 2035.' Not overconfident (definitely) but not uncertain (maybe). 'Probably' is the most versatile and natural prediction word in English!"
        },
        {
          type: "mc",
          q: "Sofía says: 'I'm sure AI ___ create new jobs — not just eliminate them.'",
          options: ["will", "is going to", "is"],
          a: "will",
          tip: "'I'm sure' = strong personal opinion. Opinion expressions pair with WILL for future predictions.",
          successTip: "'I'm sure + WILL' = expressing conviction about the future 💪. Even when the topic is data-heavy (like AI and jobs), if you're expressing YOUR belief, use WILL. 'I'm sure' signals your personal stance — the grammar reflects your voice, not cited data!"
        },
        {
          type: "order",
          q: "Traduce: 'Los expertos dicen que la economía va a mejorar.'",
          words: ["Experts", "say", "the", "economy", "is", "going", "to", "improve."],
          a: "Experts say the economy is going to improve.",
          extra: ["will", "probably", "I think"],
          tip: "'Experts say' = reporting evidence from an external source. Use GOING TO.",
          successTip: "'Experts say / Studies show / Research indicates' = evidence language → GOING TO 📈. You're reporting what someone else found, not giving your own guess. Contrast: 'I think the economy will improve' (WILL = your opinion) vs 'Experts say it's going to improve' (GOING TO = their evidence)!"
        },
        {
          type: "mc",
          q: "Dr. Chen asks Sofía: 'In your opinion, what ___ be the biggest challenge of the 21st century?'",
          options: ["will", "is going to", "is"],
          a: "will",
          tip: "'In your opinion' = asking for a personal view. Opinion questions and answers use WILL.",
          successTip: "'In your opinion + WILL' = classic opinion structure 🗣️. 'In my opinion, climate change will be the biggest challenge.' 'In your opinion, who will win?' These questions always trigger WILL because they ask for YOUR personal prediction, not cited facts!"
        },
        {
          type: "input",
          q: "Dr. Chen says: 'Looking at the data, unemployment ___ ___ ___ decrease next year.'",
          a: "is going to",
          alts: ["Is going to", "'s going to"],
          tip: "'Looking at the data' = citing evidence. Three words. Use GOING TO.",
          successTip: "'Looking at the data' = evidence phrase → GOING TO 📉. Any phrase that signals you're citing evidence — 'looking at the data', 'based on the research', 'the figures show' — triggers GOING TO. You're reporting what the data says, not guessing personally!"
        },
        {
          type: "match",
          q: "Match the prediction types to the correct grammar:",
          pairs: [
            { left: "I think...", right: "Personal opinion → WILL" },
            { left: "Studies show...", right: "Evidence-based → GOING TO" },
            { left: "The data suggests...", right: "Research-based → GOING TO" }
          ],
          a: "completed",
          successTip: "The evidence vs opinion rule applies to all topics 🌍. Your opinion (I think, I believe, I'm sure) = WILL. Cited evidence (studies show, data suggests, polls indicate) = GOING TO. Master this pattern and you'll handle any future topic — weather, politics, science, sports — correctly!"
        },
        {
          type: "mc",
          q: "Dr. Chen says: 'I believe climate change ___ affect every country on Earth.'",
          options: ["will", "is going to", "affects"],
          a: "will",
          tip: "'I believe' = expressing a personal conviction. Use WILL for the future.",
          successTip: "Climate talk = one of the most important future topics 🌡️. 'I believe climate change will affect...' / 'I think temperatures will rise...' / 'I'm sure we'll find solutions...' WILL is perfect for these long-term, opinion-based global predictions. Essential vocabulary for any educated conversation!"
        }
      ]
    },

    // ===================================================================
    // SET 9: COMMON MISTAKES
    // ===================================================================
    {
      id: "m6-u2-s9",
      title: "Set 9: Common Mistakes",
      context: `*Toronto University — Week 6, Writing Workshop*

Sofía submits her first English essay. Professor Chen returns it with corrections.
"Your ideas are excellent, Sofía — but I found some grammar patterns to fix," she says.
Sofía reads the comments. The mistakes all involve WILL and GOING TO.
"I wrote 'I will going to the cinema tomorrow' — that's wrong?" Sofía asks, surprised.
"Yes — you can't mix them," says Professor Chen. "You also wrote 'she wills come' — will never takes an -s."
Sofía takes notes carefully. "I'll fix everything — I don't think I will make these mistakes again!"

*Universidad de Toronto — Semana 6, Taller de Escritura*

Sofía entrega su primer ensayo en inglés. La profesora Chen lo devuelve con correcciones.
"Tus ideas son excelentes, Sofía — pero encontré algunos patrones gramaticales para corregir", dice.
Sofía lee los comentarios. Todos los errores involucran WILL y GOING TO.
"¿Escribí 'I will going to the cinema tomorrow' — eso está mal?" pregunta Sofía, sorprendida.
"Sí — no puedes mezclarlos", dice la profesora Chen. "También escribiste 'she wills come' — will nunca lleva -s."
Sofía toma notas cuidadosamente. "¡Corregiré todo — no creo que vuelva a cometer estos errores!"`,

      questions: [
        {
          type: "mc",
          q: "Sofía wrote: 'I have tickets. Tomorrow I ___ to the concert.' Which is correct?",
          options: ["am going", "will go", "go"],
          a: "am going",
          tip: "Tickets = confirmed plan with evidence. Use AM GOING (present continuous for future).",
          successTip: "Confirmed plans = present continuous OR going to ✈️🎫. 'I'm going to the concert' / 'I'm going to fly to Spain' / 'I'm going to the doctor on Monday.' Tickets, reservations, appointments all signal GOING TO — not WILL. Very common Spanish-speaker mistake!"
        },
        {
          type: "mc",
          q: "Professor Chen shows this sentence. Which version is CORRECT?",
          options: ["I think she'll like it.", "I think she will to like it.", "I think she going to like it."],
          a: "I think she'll like it.",
          tip: "After WILL = base verb only. NEVER 'will to like' or 'will going'.",
          successTip: "WILL + BASE VERB — never 'will to verb' ❌. This is a direct transfer error from Spanish ('voy a gustar'). In English: 'will like' ✓ / 'will to like' ✗ / 'will going' ✗. The base verb goes straight after will — no 'to', no '-ing'!"
        },
        {
          type: "order",
          q: "Sofía has cinema tickets for tomorrow. Traduce: 'Mañana voy al cine.'",
          words: ["Tomorrow", "I'm", "going", "to", "the", "cinema."],
          a: "Tomorrow I'm going to the cinema.",
          extra: ["will", "I'll", "definitely"],
          tip: "She has tickets = definite plan. Use present continuous I'm going — NOT will.",
          successTip: "Future plans = present continuous ✓. 'I'm going to the cinema.' / 'She's flying to London.' / 'We're visiting Grandma on Sunday.' Definite future plans use this pattern — NOT will. This is a crucial difference between English and Spanish!"
        },
        {
          type: "mc",
          q: "Professor Chen highlights a sentence. Which one is grammatically WRONG?",
          options: ["I will to help you.", "I'll help you.", "I will help you."],
          a: "I will to help you.",
          tip: "NEVER 'will to'. Will is a modal verb — it takes a base verb directly, no 'to'.",
          successTip: "Modal verbs NEVER use 'to' before the verb ❌. 'I will help' ✓ / 'I can help' ✓ / 'I should help' ✓ — never 'will to help', 'can to help', 'should to help'. This rule applies to all modal verbs, not just WILL. One rule, many verbs!"
        },
        {
          type: "input",
          q: "Sofía's essay says: 'I will going to the party.' Fix it: I ___ to the party.",
          a: "will go",
          alts: ["Will go", "'ll go", "am going"],
          tip: "NEVER 'will going'. Choose either 'will go' OR 'am going' — you can't mix them.",
          successTip: "Will go ✓ OR Am going ✓ — NEVER 'will going' ❌. You cannot mix WILL and -ING. Think of them as two separate lanes: WILL lane = will + base verb. GOING TO lane = am/is/are + going to + base verb. Stay in one lane!"
        },
        {
          type: "mc",
          q: "Sofía has her passport and flight booked. She says: 'I ___ fly to Spain next month.'",
          options: ["am going to", "will", "am will"],
          a: "am going to",
          tip: "Passport + booked flight = firm evidence of a plan. Use GOING TO.",
          successTip: "Passport + ticket = proof = GOING TO ✈️. Never 'am will' (this doesn't exist in English!). When you have physical evidence of your plan — tickets, reservations, documents — GOING TO is the natural choice. 'Will' would suggest you're just imagining it, not that it's booked!"
        },
        {
          type: "order",
          q: "Sofía's friend asks for help RIGHT NOW. Traduce: 'Te ayudaré.'",
          words: ["I'll", "help", "you."],
          a: "I'll help you.",
          extra: ["am going to", "will be", "helping"],
          tip: "Spontaneous decision made at the moment of speaking = WILL. Use the contraction I'll.",
          successTip: "Spontaneous offers and decisions = WILL 💡. 'I'll help you!' / 'I'll get that!' / 'I'll answer the phone!' You decide AT THAT MOMENT — not before. GOING TO = already planned. WILL = decided just now. This distinction makes your English sound natural!"
        },
        {
          type: "mc",
          q: "Professor Chen shows another error. Which sentence is grammatically WRONG?",
          options: ["She wills come.", "She'll come.", "She will come."],
          a: "She wills come.",
          tip: "WILL = modal verb. Modal verbs NEVER change form — no -s, no -ed, no -ing.",
          successTip: "Will NEVER adds -s ❌. I will / You will / He will / She will / They will — always the same form. Modal verbs (will, can, should, might, must) never conjugate. This is very different from regular verbs where 'she goes', 'he runs', etc. Modals = no change, ever!"
        },
        {
          type: "input",
          q: "Fix Sofía's sentence: 'Tomorrow I will going swimming.' → Tomorrow ___",
          a: "I will go swimming",
          alts: ["I'll go swimming", "I'm going swimming"],
          tip: "Remove '-ing' after 'will'. Either 'will go' or 'am going' — not 'will going'.",
          successTip: "Three ways to express a future plan correctly 📝: 'I will go swimming' (formal) / 'I'll go swimming' (casual spoken) / 'I'm going swimming' (definite plan). Never 'I will going' — that mixes two structures that don't combine. Pick one structure and stick to it!"
        },
        {
          type: "mc",
          q: "Professor Chen gives Sofía a final test. Which sentence is 100% correct?",
          options: ["I think it'll rain.", "I think it will to rain.", "I think it going to rain."],
          a: "I think it'll rain.",
          tip: "Check all three rules: I think + WILL (✓) + base verb (rain, not 'to rain') + contraction (it'll).",
          successTip: "Error-free checklist ✅: ❌ 'will to verb' — never ❌ 'will + -ing' — never ❌ 'she wills' — never ✓ 'I think it'll rain' — perfect! You combined: opinion expression (I think) + contraction (it'll) + base verb (rain). That's native-level accuracy. Professor Chen is very proud!"
        }
      ]
    },

    // ===================================================================
    // SET 10: MASTERY CHALLENGE
    // ===================================================================
    {
      id: "m6-u2-s10",
      title: "Set 10: Mastery Challenge",
      context: `*Toronto — End of Month 1, Sofía's first video call home*

It's the end of Sofía's first month in Toronto. She video-calls her family in Mexico City.
Her younger brother Diego asks her to teach him some English.
"Okay, Diego — let's practice making predictions!" says Sofía.
She tests everything she's learned: WILL vs GOING TO, contractions, prediction words, opinion expressions.
Her mother listens and smiles. "You sound so different already, mija. I'm certain your English will be perfect by the end of the year."
Sofía laughs. "I'll definitely try — and I'll probably make a few more mistakes first!"

*Toronto — Fin del Mes 1, primera videollamada de Sofía a casa*

Es el final del primer mes de Sofía en Toronto. Llama a su familia en Ciudad de México por videollamada.
Su hermano menor Diego le pide que le enseñe algo de inglés.
"Bien, Diego — ¡practiquemos haciendo predicciones!" dice Sofía.
Pone a prueba todo lo que ha aprendido: WILL vs GOING TO, contracciones, adverbios de predicción, expresiones de opinión.
Su madre escucha y sonríe. "Ya suenas tan diferente, mija. Estoy segura de que tu inglés será perfecto al final del año."
Sofía ríe. "Definitivamente lo intentaré — ¡y probablemente cometeré algunos errores más primero!"`,

      questions: [
        {
          type: "mc",
          q: "Sofía sees Diego about to spill his drink on the keyboard. She shouts:",
          options: ["Watch out! You're going to spill that!", "I think you'll spill that!", "You will spill that!"],
          a: "Watch out! You're going to spill that!",
          tip: "She SEES it happening. Immediate visible danger = GOING TO, not WILL.",
          successTip: "Immediate visible danger → GOING TO ⚠️. 'Watch out!' is almost always followed by GOING TO because you're reacting to something you can SEE about to happen. 'Watch out, you're going to fall!' / 'Careful, it's going to break!' React to evidence = GOING TO!"
        },
        {
          type: "mc",
          q: "Diego asks: 'Do you have plans for the weekend?' Sofía says: 'Yes, I ___ visit Niagara Falls — I already have my ticket!'",
          options: ["am going to", "will", "am"],
          a: "am going to",
          tip: "She already has her ticket = confirmed plan with evidence. GOING TO answers questions about plans.",
          successTip: "Questions about plans get GOING TO answers 📅. 'Do you have plans?' → 'Yes, I'm going to...' Because plans = something already decided and often evidence-backed. If you said 'I will visit Niagara Falls', it would sound like you're deciding spontaneously right now — not that it's already planned!"
        },
        {
          type: "order",
          q: "Traduce: 'Según las encuestas, probablemente ganarán.'",
          words: ["According", "to", "polls,", "they'll", "probably", "win."],
          a: "According to polls, they'll probably win.",
          extra: ["they go", "definitely", "I think"],
          tip: "Citing polls + adding 'probably' = your interpretation of the data. WILL is natural here.",
          successTip: "Advanced nuance: 'According to polls, they'll probably win' = you cite evidence but filter it through your interpretation with 'probably' 📊. This is B1-level precision. Compare: 'Polls show they're going to win' (direct fact) vs 'They'll probably win' (your interpretation of the data). Both are correct in different contexts!"
        },
        {
          type: "mc",
          q: "Sofía's aunt is pregnant. She says: 'She ___ have a baby in April!'",
          options: ["is going to", "will", "might"],
          a: "is going to",
          tip: "Pregnancy = confirmed, evidence-based future event. Use GOING TO.",
          successTip: "Life events with clear evidence = GOING TO 👶. Pregnancy, a scheduled surgery, a booked trip, a signed contract — all use GOING TO because there's real, physical evidence. 'She's going to have a baby!' / 'They're going to get married in June!' These are facts, not guesses!"
        },
        {
          type: "input",
          q: "Diego spills his drink right now. He says immediately: 'Oh no! I ___ clean it up!'",
          a: "will",
          alts: ["Will", "'ll"],
          tip: "He decides RIGHT NOW, in this moment. Spontaneous decisions = WILL.",
          successTip: "Spontaneous decisions at the moment of speaking = WILL 💡. 'I'll clean it up!' / 'I'll call her right now!' / 'I'll take that one!' You didn't plan it — you just decided. This is one of the most common uses of WILL in real daily life. Very natural and immediate!"
        },
        {
          type: "match",
          q: "Sofía teaches Diego: Match each situation to the correct grammar:",
          pairs: [
            { left: "You see dark clouds right now", right: "It's going to rain → GOING TO" },
            { left: "You think it might rain", right: "I think it will rain → WILL" },
            { left: "The weather app says sunny weather", right: "It's not going to rain → GOING TO" }
          ],
          a: "completed",
          successTip: "Sofía just taught her brother the full rule 🎓! SEE evidence right now = GOING TO 👁️. THINK / personal opinion = WILL 💭. REPORT evidence from a source = GOING TO 📱. same situation, three different framings — and the grammar changes each time. This is real mastery!"
        },
        {
          type: "mc",
          q: "The phone rings during the video call. Sofía says immediately:",
          options: ["I'll answer it!", "I'm going to answer it!", "I answer it!"],
          a: "I'll answer it!",
          tip: "She decides RIGHT NOW to answer. Spontaneous decision = WILL (I'll).",
          successTip: "Spontaneous offers in the home = I'll ✓ 📞. 'I'll get the door!' / 'I'll answer the phone!' / 'I'll make the tea!' All decided at the exact moment of speaking. GOING TO would mean she planned to answer before it rang — which makes no sense! WILL = instant, natural, daily!"
        },
        {
          type: "order",
          q: "Traduce: 'Basándome en mi experiencia, creo que definitivamente tendrá éxito.'",
          words: ["Based", "on", "my", "experience,", "I", "think", "it'll", "definitely", "succeed."],
          a: "Based on my experience, I think it'll definitely succeed.",
          extra: ["is going to", "probably", "might"],
          tip: "'I think' = WILL. Add 'definitely' for strong emphasis. Use the contraction 'it'll'.",
          successTip: "B1 complexity mastered 🏆! You combined: introductory phrase (Based on my experience) + opinion expression (I think) + contraction (it'll) + prediction adverb (definitely) + base verb (succeed). This is sophisticated, natural English. Native speakers use all these elements together constantly!"
        },
        {
          type: "mc",
          q: "Which sentence shows the speaker has NO direct evidence — only a personal opinion?",
          options: ["I think it'll be a great trip.", "The schedule says it's going to be amazing.", "I booked it — I'm going to love it."],
          a: "I think it'll be a great trip.",
          tip: "'I think' = personal opinion with no external evidence. This triggers WILL.",
          successTip: "Evidence detection = a real skill 🎯. 'I think it'll...' = opinion (no evidence, WILL ✓). 'The schedule says it's going to...' = citing external evidence (GOING TO ✓). 'I booked it — I'm going to...' = personal confirmed plan with evidence (GOING TO ✓). You can now read any sentence and identify the grammar logic behind it!"
        },
        {
          type: "mc",
          q: "Diego asks: 'What's your prediction for YOUR English at the end of the year?' Sofía answers:",
          options: ["I think my English will be much better!", "My English is going to better.", "My English wills be better."],
          a: "I think my English will be much better!",
          tip: "Opinion about her own future progress. 'I think' + WILL + base verb 'be'. No -s on will.",
          successTip: "Unit 2 Complete! 🎉🚀 Sofía can now: 🔮 Make natural predictions with WILL / 👁️ React to evidence with GOING TO / 💭 Express opinions: I think, I believe, I'm sure, I doubt, I bet / 📊 Grade certainty: definitely, probably, maybe, perhaps, certainly, likely / 🗣️ Use natural contractions: I'll, she'll, won't / ✅ Avoid all common mistakes. ¡Nivel B1 alcanzado, Sofía!"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET
  // ===================================================================
  readingSet: {
    id: "m6-u2-reading",
    title: "Reading: A Day of Predictions",
    category: "reading",
    questions: [
      {
        type: "passage",
        content: `**Making Predictions: A Day of News**

Good morning! I'm Sarah Chen, your news anchor, and we have lots of predictions to talk about today.

**Weather Forecast**
First, let's check the weather. Meteorologist Tom Williams says it's going to rain this afternoon. "Look at these dark clouds on the radar," Tom explains. "The storm is definitely going to reach the city by 3 PM." However, Tom also predicts that the rain will stop by evening. "I think we'll see sunshine tomorrow," he says with a smile.`
      },
      {
        type: "mc",
        q: "According to Tom Williams, what time will the storm reach the city?",
        options: ["3 PM", "7 PM", "Tomorrow morning"],
        a: "3 PM",
        successTip: "Reading for specific detail 📖. The text says 'definitely going to reach the city by 3 PM.' Finding exact facts = key reading skill. Always scan for numbers and times — they're easy to locate and often tested!"
      },
      {
        type: "mc",
        q: "Tom uses 'it's going to rain' because:",
        options: ["He sees evidence on the radar", "It's just his personal opinion", "He is guessing randomly"],
        a: "He sees evidence on the radar",
        tip: "The text says 'Look at these dark clouds on the radar' — he's reacting to visible data.",
        successTip: "Grammar in context 🎯. Radar data = evidence → GOING TO. The text explicitly shows WHY he uses GOING TO: 'Look at these dark clouds on the radar.' This is exactly the evidence rule — visible, concrete data triggers GOING TO every time!"
      },
      {
        type: "mc",
        q: "Tom says: 'I think we'll see sunshine tomorrow.' He uses WILL because:",
        options: ["It's his prediction about tomorrow — just his opinion", "He sees sunshine right now", "Tomorrow is already confirmed by data"],
        a: "It's his prediction about tomorrow — just his opinion",
        successTip: "same person, two different grammar choices! Tom says 'it's going to rain' (he sees radar evidence) AND 'I think we'll see sunshine' (his personal opinion about tomorrow). Evidence vs opinion = GOING TO vs WILL. One meteorologist, two grammar choices in one paragraph — beautiful!"
      },
      {
        type: "passage",
        content: `**Sports Update**
In sports news, the championship game is tonight! Sports analyst Mike Rodriguez has his predictions. "I believe the Bears will win," Mike tells us. "They've been playing really well this season." He admits the Tigers are strong too. "It'll probably be a close game," he adds.

According to ticket sales, the stadium is going to be completely full. "We've sold all 50,000 tickets," says stadium manager Lisa Park. "It's definitely going to be our biggest event of the year."`
      },
      {
        type: "mc",
        q: "Mike says 'the Bears will win' because:",
        options: ["It's his opinion based on their performance", "He has confirmed data they already won", "The game already happened"],
        a: "It's his opinion based on their performance",
        successTip: "'I believe + WILL' = opinion prediction 💭. Mike thinks the Bears will win based on their season — but it's his interpretation, not a confirmed fact. Perfect example of WILL for opinion-based sports predictions!"
      },
      {
        type: "input",
        q: "The stadium will be full because they've ___ all 50,000 tickets.",
        a: "sold",
        alts: ["Sold","sold all", "sold out", "already sold", "completely sold", "now sold"],
        successTip: "Sold all tickets = evidence of a full stadium 🎟️. This is why Lisa Park uses 'is going to be completely full' — the sold-out tickets are the physical evidence. Sold tickets = GOING TO. It's the same logic as having a booked flight or a confirmed reservation!"
      },
      {
        type: "passage",
        content: `**Election News**
The mayoral election is next week. Recent polls show that candidate Sarah Martinez is going to win. "The polls give her a 15-point lead," reports analyst James Wong. "Based on this data, she's most likely going to be our next mayor."

However, candidate Tom Brown says: "I don't think the polls tell the whole story. I believe the voters will support our message." Political expert Dr. Emily Foster offers her view: "I think Martinez will probably win, but Brown is right — voters might change their minds. Elections can surprise us."`
      },
      {
        type: "mc",
        q: "The article uses 'is going to win' for Sarah Martinez because:",
        options: ["Polls provide data/evidence", "It's just the analyst's opinion", "The election already happened"],
        a: "Polls provide data/evidence",
        tip: "Polls = data = evidence → GOING TO. The text says 'recent polls SHOW' — citing evidence.",
        successTip: "Polls → GOING TO 📊. 'Recent polls show she's going to win.' The data IS the evidence. Contrast with Tom Brown's response: 'I believe voters WILL support us' — he uses WILL because he's expressing his personal belief, not citing data. same election, two grammar choices!"
      },
      {
        type: "mc",
        q: "Tom Brown uses WILL ('voters will support') because:",
        options: ["He expresses his personal belief, not poll data", "He has new secret poll data", "The election result is confirmed"],
        a: "He expresses his personal belief, not poll data",
        successTip: "Contrasting grammar in one news story 📰. Analyst (polls/evidence) = GOING TO. Tom Brown (personal belief) = WILL. Dr. Foster (her opinion + hedge) = 'will probably'. Three people, three different grammar choices — all correct for their respective levels of evidence and certainty!"
      },
      {
        type: "input",
        q: "Dr. Foster thinks Martinez will ___ win.",
        a: "probably",
        alts: ["Probably","Probably", "likely", "Likely", "certainly", "Certainly","probably still", "most likely", "almost certainly", "very likely", "surely still"],
        successTip: "'Probably' = cautious but likely prediction 📊. Dr. Foster is an expert — she uses 'probably' to show she's informed but not overconfident. This is intellectually honest: she has data but acknowledges uncertainty. 'Probably' is the prediction word of educated, careful people!"
      },
      {
        type: "passage",
        content: `**Technology Corner**
Tech expert David Lee shared his predictions about artificial intelligence. "I'm certain that AI will change many jobs in the next ten years," he says. "Some jobs will disappear, but new ones will appear too."

When asked about electric cars, David adds: "Looking at current trends, I'd say electric vehicles are going to dominate the market by 2035." 

That's all for today's news. Some predictions are based on evidence like weather radar and polls. Others are personal opinions about what we think will happen. Either way, the future is always interesting!`
      },
      {
        type: "mc",
        q: "David says 'AI will change many jobs' because:",
        options: ["It's his strong personal conviction about the future", "AI has already changed all jobs", "He is citing a specific study"],
        a: "It's his strong personal conviction about the future",
        successTip: "'I'm certain + WILL' = expressing maximum personal conviction 💪. David uses WILL because he's giving his expert opinion — his personal conviction — not citing a specific study. 'I'm certain that AI will change...' is opinion-based, even from an expert. His certainty is personal, not data-cited!"
      },
      {
        type: "mc",
        q: "David uses 'are going to dominate' for electric cars because:",
        options: ["He's looking at current trends as evidence", "It's just a wild guess", "Electric cars already dominate the market"],
        a: "He's looking at current trends as evidence",
        tip: "'Looking at current trends' = citing observable data as evidence → GOING TO.",
        successTip: "Trend data = evidence → GOING TO 📈. 'Looking at current trends, electric vehicles are going to dominate.' He's not guessing — he's reading the data. Contrast with his AI comment ('I'm certain AI will change...' = personal conviction). same expert, same interview, two different grammar choices based on whether he's citing data or expressing opinion!"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET
  // ===================================================================
  reviewSet: {
    id: "m6-u2-review",
    title: "Unit 2 Review — Predictions Mastery",
    category: "review",
    questions: [
      {
        type: "mc",
        q: "Which prediction is based on VISIBLE EVIDENCE right now?",
        options: ["Look! He's going to fall!", "I think he'll fall.", "Maybe he'll fall someday."],
        a: "Look! He's going to fall!",
        tip: "'Look!' = you see it happening. Immediate visible evidence = GOING TO.",
        successTip: "'Look!' → GOING TO ⚠️. When you react to something you can see, GOING TO is the natural choice. 'I think he'll fall' = just your opinion (WILL). 'Look, he's going to fall!' = you SEE it (GOING TO). The key is whether you're reacting to visible evidence or expressing a personal thought."
      },
      {
        type: "mc",
        q: "Which sentence expresses a PERSONAL OPINION about the future?",
        options: ["I believe they'll win.", "The forecast says it's going to rain.", "I booked it — I'm going to the concert."],
        a: "I believe they'll win.",
        successTip: "'I believe + WILL' = opinion 💭. The forecast = external evidence (GOING TO). 'I booked it' = personal confirmed plan (GOING TO). Only 'I believe' signals a personal prediction without cited evidence — which is exactly when WILL is the correct choice!"
      },
      {
        type: "order",
        q: "Traduce: 'Probablemente llueva mañana.' (tu opinión personal)",
        words: ["It'll", "probably", "rain", "tomorrow."],
        a: "It'll probably rain tomorrow.",
        extra: ["definitely", "maybe", "I think"],
        tip: "Word order: subject + 'll + PROBABLY + verb. Probably goes AFTER will.",
        successTip: "Word order: Subject + 'll + PROBABLY + verb ✓ 📐. 'It'll probably rain' = correct. 'Probably it'll rain' = wrong ❌. This word order rule applies to all prediction adverbs: definitely, probably, maybe, certainly, likely — all go AFTER 'll/will!"
      },
      {
        type: "match",
        q: "Match the opinion expressions to their certainty level:",
        pairs: [
          { left: "I'm certain", right: "95-100% sure" },
          { left: "I think", right: "About 60% sure" },
          { left: "I guess", right: "About 40% sure" }
        ],
        a: "completed",
        successTip: "Certainty scale 📊: I guess (40%) → I think (60%) → I believe (70%) → I'm sure (85%) → I'm certain / I'm positive (95-100%). Choose based on your actual level of confidence. This precision makes your English sound nuanced and natural — not just correct, but sophisticated!"
      },
      {
        type: "mc",
        q: "What is the contraction of 'I will'?",
        options: ["I'll", "I'd", "I've"],
        a: "I'll",
        successTip: "Don't confuse the contractions! 🔑 I'll = I will. I'd = I would or I had. I've = I have. These sound similar but mean very different things. In speech, context usually makes the meaning clear, but in writing, each one is distinct. I'll see you tomorrow (future). I'd love to (conditional). I've done it (perfect)!"
      },
      {
        type: "input",
        q: "Complete the negative contraction: 'I ___ be late, I promise.'",
        a: "won't",
        alts: ["Won't"],
        tip: "Negative contraction of WILL. One word. The only irregular contraction — not 'willn't'!",
        successTip: "Won't = will not — and it's the ONLY irregular WILL contraction 🚫. All others are regular: I'll, she'll, they'll, we'll. But 'will not' → 'won't' — not 'willn't'! This makes won't unique and worth memorising separately. Use it constantly: 'I won't forget.' / 'She won't come.'"
      },
      {
        type: "mc",
        q: "Which prediction word means 'almost 100% certain'?",
        options: ["Definitely", "Maybe", "Perhaps"],
        a: "Definitely",
        successTip: "Definitely = maximum certainty in informal/neutral English 💯. 'She'll definitely pass.' / 'It'll definitely rain.' 'Maybe' = only 50%. 'Perhaps' = maybe but formal. For 100% confidence in casual conversation, 'definitely' is your go-to word. 'Certainly' is its formal equivalent!"
      },
      {
        type: "mc",
        q: "Sofía has a booked flight and hotel. She says: 'I ___ visit Niagara Falls next weekend.'",
        options: ["am going to", "will", "might"],
        a: "am going to",
        tip: "Booked flight + hotel = confirmed plan with physical evidence. GOING TO.",
        successTip: "Confirmed plans = GOING TO 🏨✈️. Booked flights, hotel reservations, doctor appointments, bought tickets — all physical evidence of a plan → GOING TO. 'Will' would suggest she's deciding spontaneously right now, which contradicts the fact that she already booked everything!"
      },
      {
        type: "match",
        q: "Match each context to the correct grammar choice:",
        pairs: [
          { left: "Weather forecast on your phone", right: "It's going to rain → GOING TO" },
          { left: "Your personal opinion about tomorrow", right: "I think it will rain → WILL" },
          { left: "You see clouds right now", right: "It's going to rain → GOING TO" }
        ],
        a: "completed",
        successTip: "Three scenarios, one grammar rule 🌦️. Forecast = evidence (GOING TO). Personal opinion = WILL. Visible clouds = evidence (GOING TO). Notice that forecast and visible clouds both trigger GOING TO for the same reason: you're reacting to something real, not just thinking. Opinion = only in your head = WILL!"
      },
      {
        type: "mc",
        q: "Which sentence is 100% grammatically correct?",
        options: ["I think she'll probably come.", "I think she will to come.", "I think she going to come."],
        a: "I think she'll probably come.",
        tip: "Check: I think + WILL (contraction she'll) + prediction word (probably) + base verb (come). All correct.",
        successTip: "Unit 2 Complete — Mastery Achieved! 🎉🏆 You can now: 🔮 Make predictions with WILL and GOING TO / 💭 Express opinions naturally: I think, I believe, I'm sure, I doubt, I bet / 📊 Grade certainty: definitely, probably, maybe, perhaps, certainly, likely / 🗣️ Use contractions: I'll, she'll, won't / ✅ Avoid all common mistakes. ¡Sofía would be proud! Ready for Unit 3! 🚀"
      }
    ]
  }
};
